// Колоните на „Гости" — един ред = един телефон. Броячите са колони, за да
// се подрежда по „най-много неявявания" и „най-много посещения".

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { EM_DASH, parseDecimal } from "../../../../components/dataTable";
import PhoneLink from "../../../../components/PhoneLink";
import type { ReservationGuestRow } from "../../../../api/reportsReservations";
import { formatMomentWithDate, noShowRate } from "./reservationReportFormat";

const helper = createColumnHelper<ReservationGuestRow>();

const MONO = "font-pos-mono text-[12.5px] font-semibold tabular-nums";
// Като текстовата клетка на таблицата — телефонът само става връзка.
const PHONE_CELL = "text-[13px] font-medium text-ink";

const countColumn = (id: keyof ReservationGuestRow, header: string, size = 96) =>
  helper.accessor((row) => row[id] as number, {
    id,
    header,
    size,
    meta: {
      cell: { kind: "num" },
      total: "sum",
      hideBelow: "tablet",
      excel: { format: "number" },
    },
  });

export const useGuestsColumns = () => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      helper.accessor((row) => row.guest_name, {
        id: "guest_name",
        header: t("reportsPage.rvColumnName"),
        size: 200,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "text" },
          alwaysVisible: true,
          mobileRole: "title",
          excel: { format: "text" },
        },
      }),

      // Телефонът набира при клик и НЕ отваря историята на госта (кликът на
      // реда) — собствен рендер вместо `meta.cell`.
      helper.accessor((row) => row.phone, {
        id: "phone",
        header: t("reportsPage.rvColumnPhone"),
        size: 140,
        cell: (context) => (
          context.getValue() ? (
            <PhoneLink phone={context.getValue()} className={PHONE_CELL} />
          ) : (
            // Стар запис без телефон — „—" като празната текстова клетка.
            <span className="text-faint">{EM_DASH}</span>
          )
        ),
        meta: {
          filter: { kind: "text" },
          alwaysVisible: true,
          mobileRole: "meta",
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.reservations, {
        id: "reservations",
        header: t("reportsPage.rvKpiReservations"),
        size: 110,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          mobileRole: "strong",
          excel: { format: "number" },
        },
      }),

      countColumn("completed", t("reportsPage.rvStatus_completed")),
      countColumn("cancelled", t("reportsPage.rvStatus_cancelled")),

      helper.accessor((row) => row.expired, {
        id: "expired",
        header: t("reportsPage.rvStatus_expired"),
        size: 104,
        cell: (context) => (
          <span
            className={`${MONO} ${context.getValue() > 0 ? "text-danger-ink" : "text-faint"}`}
          >
            {context.getValue()}
          </span>
        ),
        meta: {
          align: "right",
          total: "sum",
          totalCell: { kind: "num" },
          excel: { format: "number" },
        },
      }),

      // Процентът е по резервациите без отказаните — отказаната навреме не е
      // неявяване. Без такива резервации → null → тире.
      helper.accessor((row) => noShowRate(row), {
        id: "no_show_rate",
        header: t("reportsPage.rvColumnNoShowRate"),
        size: 104,
        meta: {
          cell: { kind: "pct", decimals: 0 },
          hideBelow: "tablet",
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => row.guests, {
        id: "guests",
        header: t("reportsPage.rvKpiGuests"),
        size: 92,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          hideBelow: "tablet",
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => parseDecimal(row.revenue), {
        id: "revenue",
        header: t("reportsPage.rvKpiRevenue"),
        size: 110,
        meta: {
          cell: { kind: "money", mutedZero: true },
          total: "sum",
          excel: { format: "money" },
        },
      }),

      helper.accessor((row) => row.last_visit_at, {
        id: "last_visit_at",
        header: t("reportsPage.rvColumnLastVisit"),
        size: 150,
        cell: (context) => (
          <span
            className={`${MONO} ${context.getValue() === null ? "text-faint" : "text-ink"}`}
          >
            {formatMomentWithDate(context.getValue())}
          </span>
        ),
        meta: { align: "right", hideBelow: "tablet", excel: { format: "text" } },
      }),

      helper.accessor((row) => row.last_reservation_at, {
        id: "last_reservation_at",
        header: t("reportsPage.rvColumnLastReservation"),
        size: 150,
        cell: (context) => (
          <span className={`${MONO} text-muted`}>
            {formatMomentWithDate(context.getValue())}
          </span>
        ),
        meta: { align: "right", defaultHidden: true, excel: { format: "text" } },
      }),

      helper.accessor((row) => row.first_reservation_at, {
        id: "first_reservation_at",
        header: t("reportsPage.rvColumnFirstReservation"),
        size: 150,
        cell: (context) => (
          <span className={`${MONO} text-muted`}>
            {formatMomentWithDate(context.getValue())}
          </span>
        ),
        meta: { align: "right", defaultHidden: true, excel: { format: "text" } },
      }),
    ],
    [t],
  );
};
